/* eslint-disable no-nested-ternary */
import {
	Button,
	DataTable,
	Input,
	ModalConfirmation,
	Typography,
} from 'design_system/src';
import { updateTaxDataFinancialForm } from 'models/financial-form/controllers/financial-form.controller';
import {
	FinancialFormTypeEnum,
	FormStatus,
} from 'models/financial-form/enums/financial-form.enums';
import { TaxData } from 'models/financial-form/interfaces/financial-form.interfaces';
import { LoanStatus } from 'models/loan/enums/loan.enum';
import { RegimeDescriptionEnum } from 'models/ozoner/enums/ozoner.enums';
import { toasterOptions } from 'models/shared/enums/toaster.enum';
import { commonErrorHandler } from 'models/shared/helpers/errors';
import { toasterNotification } from 'models/shared/helpers/toasterNotification';
import React, { useContext, useMemo, useState } from 'react';
import { EvidenceDocumentType } from 'models/shared/types/documents';
import { ozonerContext } from '../OzonerContext';

interface DataType {
	value: string | React.ReactNode;
	label?: string | React.ReactNode;
	valueColor?: string;
	onClick?: any;
}

const regimeOptions = Object.entries(RegimeDescriptionEnum).map(
	([, value]) => ({
		label: value,
		value,
	}),
);

function TaxDataOzoner() {
	const { financialForm, updateFormData } = useContext(ozonerContext);

	const taxData = (financialForm?.form as any)?.taxData as TaxData | undefined;

	const [openEdit, setOpenEdit] = useState(false);
	const [saving, setSaving] = useState(false);
	const [draft, setDraft] = useState<Omit<TaxData, 'regimeCode'>>({});

	const isCreditLine =
		financialForm?.form?.type === FinancialFormTypeEnum.creditLine;

	const activeStatus = [
		FormStatus.pending,
		FormStatus.contacted,
		FormStatus.inValidation,
		FormStatus.approved,
		FormStatus.scheduled,
	];
	const editTaxData = useMemo<boolean>(
		() =>
			activeStatus.includes(financialForm?.form?.status) &&
			!!financialForm?.form?.documents?.find(
				(item) => item.typeEvidence === EvidenceDocumentType.tax_situation,
			),
		[financialForm],
	);

	const openModal = () => {
		setDraft({
			rfc: taxData?.rfc ?? '',
			rfcName: taxData?.rfcName ?? '',
			zipCode: taxData?.zipCode ?? '',
			regimeDescription: taxData?.regimeDescription,
		});
		setOpenEdit(true);
	};

	const save = () => {
		if (!financialForm?.form) return;
		setSaving(true);
		updateTaxDataFinancialForm(financialForm.form._id, draft)
			.then((updated) => {
				updateFormData({
					...(financialForm.form as any),
					taxData: (updated as any).taxData,
				});
				toasterNotification({
					msg: 'Datos fiscales actualizados',
					style: 'light',
					toasterType: toasterOptions.success,
				});
				setOpenEdit(false);
			})
			.catch(commonErrorHandler)
			.finally(() => setSaving(false));
	};

	const getRfcData = () => {
		const fieldRequired = (value: string | number | undefined) =>
			editTaxData && !value;

		const data: DataType[] = [
			{
				label: 'RFC',
				value: `${taxData?.rfc ?? 'Campo incompleto'}`,
				valueColor: fieldRequired(taxData?.rfc) ? 'red_300' : 'neutral_800',
			},
			{
				label: 'Código de régimen fiscal',
				value: taxData?.regimeCode
					? taxData?.regimeCode.toString()
					: 'Campo incompleto',
				valueColor: fieldRequired(taxData?.regimeCode)
					? 'red_300'
					: 'neutral_800',
			},
			{
				label: 'Régimen fiscal',
				value: taxData?.regimeDescription ?? 'Campo incompleto',
				valueColor: fieldRequired(taxData?.regimeCode)
					? 'red_300'
					: 'neutral_800',
			},
			{
				label: 'Nombre',
				value: `${taxData?.rfcName ?? 'Campo incompleto'}`,
				valueColor: fieldRequired(taxData?.rfcName) ? 'red_300' : 'neutral_800',
			},
			{
				label: 'Código postal',
				value: `${taxData?.zipCode ?? 'Campo incompleto'}`,
				valueColor: fieldRequired(taxData?.zipCode) ? 'red_300' : 'neutral_800',
			},
		];
		return data;
	};

	return (
		<div className="bg_neutral_0 border_neutral_400 border_solid border_1 br_sm overflow_hidden m_y_md">
			<div
				id="datos-fiscales-ozoner"
				className="p_md display_flex flex_justify_between flex_align_center"
			>
				<Typography
					scale="medium"
					weight="400"
					textColor="neutral_800"
					className="text_no_break"
				>
					Información fiscal
				</Typography>
				<Button scale="small" variant="ghost" onClick={openModal}>
					Editar
				</Button>
			</div>
			<div className="p_xs">
				<DataTable data={getRfcData()} weightLabels={2} weightValues={2} />
				<Button
					scale="small"
					className="w_100_per m_t_md"
					disabled={!editTaxData}
					onClick={openModal}
				>
					Subir datos fiscales
				</Button>
			</div>

			{openEdit && (
				<ModalConfirmation
					openConfirmation={openEdit}
					handleClose={() => setOpenEdit(false)}
					widthDesktop={40}
					isConfirmationLoading={saving}
					actions={[
						{
							label: 'Cancelar',
							color: 'grey',
							action: () => setOpenEdit(false),
						},
						{
							label: 'Guardar',
							color: 'primary',
							action: save,
							disabled: saving,
						},
					]}
					title={
						<Typography scale="large" weight="600">
							Editar información fiscal
						</Typography>
					}
					subtitle={
						<div className="display_flex flex_col">
							<Input
								title="RFC"
								name="rfc"
								type="text"
								placeholder="Ej. XAXX010101000"
								value={draft.rfc ?? ''}
								onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
									setDraft((d) => ({ ...d, rfc: e.target.value }))
								}
							/>
							<Input
								title="Régimen fiscal"
								name="regimeDescription"
								type="select"
								placeholder="Selecciona un régimen"
								value={draft.regimeDescription ?? ''}
								options={regimeOptions}
								onChange={(e: { target: { value: RegimeDescriptionEnum } }) =>
									setDraft((d) => ({ ...d, regimeDescription: e.target.value }))
								}
							/>
							<Input
								title="Nombre"
								name="rfcName"
								type="text"
								placeholder="Nombre del contribuyente"
								value={draft.rfcName ?? ''}
								onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
									setDraft((d) => ({ ...d, rfcName: e.target.value }))
								}
							/>
							<Input
								title="Código postal"
								name="zipCode"
								type="text"
								placeholder="Ej. 06600"
								value={draft.zipCode ?? ''}
								onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
									setDraft((d) => ({ ...d, zipCode: e.target.value }))
								}
							/>
						</div>
					}
				/>
			)}
		</div>
	);
}
export default TaxDataOzoner;
export {};
